/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/system/PlastronWelcomeModal.tsx
 *
 * Rôle :
 *   Modale plein écran d'accueil affichée UNE SEULE FOIS par session
 *   quand un visiteur se connecte avec un compte plastron (compte de
 *   DÉMONSTRATION en lecture seule). Le message recommande FORTEMENT
 *   d'utiliser un vrai compte personnel pour profiter pleinement du
 *   salon TNE 2026.
 *
 *   Différences avec `<PlastronGuardModal>` :
 *     - PlastronGuardModal s'ouvre à CHAQUE clic sur une action de
 *       mutation (signup, favori, save profil…) → contextuelle.
 *     - PlastronWelcomeModal s'ouvre UNE FOIS au mount, plein écran,
 *       au moment du login plastron → informationnelle / push.
 *
 *   Persistance : sessionStorage `tne.plastron.welcome.dismissed`. Si
 *   présent, ne s'affiche plus dans cette session. Reset à chaque
 *   nouveau login (le sessionStorage est purgé au close de l'onglet).
 *
 *   Le composant est monté UNE SEULE FOIS dans `<AppLayout>` à côté
 *   de `<PlastronGuardModal>`, il observe `useVisitorSession()` pour
 *   détecter le rôle plastron et déclencher l'ouverture initiale.
 *
 * Réutilisable :
 *   Composant TNE 2026 spécifique (wording français + rôle 'plastron').
 *   Pour porter, adapter le rôle attendu et le wording du CTA.
 *
 * Voir :
 *   - src/components/system/PlastronGuardModal.tsx (modale contextuelle)
 *   - src/hooks/useVisitorSession.ts (source du rôle)
 *   - src/components/ui/Modal.tsx (composant Modal partagé §8.6)
 *   - CLAUDE.md §5/§24 2026-05-20 (feature plastron).
 *
 * @author Mika
 * @since  2026-05-20
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEventIdentity } from '@/context/EventIdentityProvider';
import { useQueryClient } from '@tanstack/react-query';

import { Modal, Button } from '@/components/ui';
import { useVisitorSession, VISITOR_SESSION_QUERY_KEY } from '@/hooks/useVisitorSession';
import { apiMutate } from '@/api/client';
// FIX 2026-05-25 (sprint borne, CLAUDE.md §5 entrée 2026-05-25) :
// en mode kiosque, on ne montre PAS du tout la modale d'accueil
// (sinon impose un clic manuel après chaque login plastron, non
// désirable sur une borne publique laissée sans surveillance).
import { useKioskMode } from '@/hooks/useKioskMode';

const STORAGE_KEY = 'tne.plastron.welcome.dismissed';

export function PlastronWelcomeModal() {
  const intl = useIntl();
  const identity = useEventIdentity();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { role, status } = useVisitorSession();
  const { enabled: isKiosk } = useKioskMode();

  const [open, setOpen] = useState<boolean>(false);

  // Effet d'ouverture : à chaque login plastron, on regarde si la
  // modale a déjà été close pour cette session. Si non → ouvrir.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (role !== 'plastron') {
      // Si le visiteur n'est pas plastron, on s'assure que la modale
      // est fermée (cas de bascule de compte sans recharge).
      setOpen(false);
      return;
    }
    // FIX 2026-05-25 (sprint borne) : en mode kiosque, on saute
    // l'ouverture (la borne fonctionne sans pop-up bloquant).
    if (isKiosk) {
      setOpen(false);
      return;
    }
    // Lecture du flag sessionStorage (réinitialisé à la fermeture de
    // l'onglet). Best-effort : si le storage n'est pas accessible
    // (privé / quota), on AFFICHE la modale (l'invitant pédagogique
    // est plus important que le confort UX).
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      dismissed = false;
    }
    if (!dismissed) {
      setOpen(true);
    }
  }, [role, status, isKiosk]);

  const handleClose = useCallback(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* sessionStorage indispo (mode privé / quota) : ignore. La
         modale ne se ré-ouvrira pas tant que la page n'est pas
         rechargée (le state local `open` suffit pour la session
         courante du composant). */
    }
    setOpen(false);
  }, []);

  /**
   * « Se créer un vrai compte » : déconnecte le plastron + redirige
   * vers /role. Pattern miroir de PlastronGuardModal.
   */
  const handleCreateRealAccount = useCallback(() => {
    void (async () => {
      try {
        await apiMutate(
          '/visitor/logout.php',
          'POST',
          {},
          { kind: 'visitor-logout', offline: false },
        );
      } catch {
        /* dégradation acceptable */
      }
      await qc.invalidateQueries({ queryKey: VISITOR_SESSION_QUERY_KEY });
      handleClose();
      navigate('/role');
    })();
  }, [navigate, qc, handleClose]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={intl.formatMessage({ id: 'plastron.welcome.title' }, identity)}
      size="lg"
      closeOnOverlayClick={false}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          padding: '0.5rem 0',
        }}
      >
        {/* Bandeau d'alerte coloré, pas un toast, un encadré marquant
            pour que le visiteur comprenne dès l'ouverture qu'il n'est
            pas dans le flux normal. */}
        <div
          role="note"
          style={{
            padding: '1rem 1.25rem',
            background: 'var(--tne-color-surface-warning, #FFF5E6)',
            borderLeft: '4px solid var(--tne-color-secondary, #B8481A)',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          <FormattedMessage id="plastron.welcome.banner" />
        </div>

        <p style={{ margin: 0, fontSize: '1rem', lineHeight: 1.55 }}>
          <FormattedMessage id="plastron.welcome.body1" />
        </p>
        <p style={{ margin: 0, fontSize: '1rem', lineHeight: 1.55 }}>
          <FormattedMessage id="plastron.welcome.body2" />
        </p>

        {/* Recommandation forte : CTA principal mis en avant. */}
        <div
          style={{
            padding: '1rem 1.25rem',
            background: 'var(--tne-color-surface-success, #EFF6E7)',
            borderLeft: '4px solid var(--tne-color-tertiary, #5A8A3F)',
            borderRadius: '8px',
            fontSize: '1rem',
            lineHeight: 1.55,
          }}
        >
          <strong>
            <FormattedMessage id="plastron.welcome.recommendation.title" />
          </strong>
          <br />
          <FormattedMessage id="plastron.welcome.recommendation.body" />
        </div>

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            marginTop: '0.5rem',
          }}
        >
          <Button variant="secondary" size="md" onClick={handleClose}>
            <FormattedMessage id="plastron.welcome.btn.continueDemo" />
          </Button>
          {/* FIX 2026-05-25 (sprint borne) : « Se créer un vrai compte »
              masqué en mode kiosque (defense in depth, la modale n'est
              normalement même pas ouverte en kiosque, cf. useEffect plus
              haut, mais on s'assure que le bouton n'apparaît jamais). */}
          {!isKiosk && (
            <Button variant="primary" size="md" onClick={handleCreateRealAccount}>
              <FormattedMessage id="plastron.welcome.btn.createReal" />
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
